import { UiState } from '../state/UiState';
import { SAMPLE_IMAGES } from '../assets/sampleImages';
import { t, subscribeLocaleChange } from '../i18n/i18n';

interface SpringPoint {
  cx: number;
  cy: number;
  dx: number;
  dy: number;
  vx: number;
  vy: number;
  radius: number;
  isDragging: boolean;
  isAutoWobble?: boolean;
}

export interface CanvasInteractionCallbacks {
  onPhysicsImpulse?: (type: 'poke' | 'pull' | 'bounce') => void;
  onFileLoaded?: (file: File) => void;
  onSampleChanged?: (index: number) => void;
  onWobblePosPicked?: (u: number, v: number) => void;
  onUpgradeRequested?: (featureName: string) => void;
}

export class CanvasInteraction {
  private container: HTMLElement;
  private uiState: UiState;
  private callbacks: CanvasInteractionCallbacks;

  private canvasWrapperEl: HTMLElement;
  private canvas: HTMLCanvasElement;
  private gl: WebGLRenderingContext | null = null;
  private touchRingEl: HTMLElement;
  private wobbleGuideContainerEl!: HTMLElement;
  private wobbleRangeCircleEl!: HTMLElement;
  private wobblePinEl!: HTMLElement;
  private isPickingWobblePos: boolean = false;
  private isTemporarilyShowingRange: boolean = false;
  private tempRangeTimer: number | null = null;
  private watermarkEl: HTMLElement;
  private dropOverlayEl: HTMLElement;
  private sampleBarEl: HTMLElement;
  private hintTextEl!: HTMLElement;
  private sampleLabelEl!: HTMLElement;
  private dropTextEl!: HTMLElement;

  // WebGL リソース
  private program: WebGLProgram | null = null;
  private texture: WebGLTexture | null = null;
  private uResolutionLoc: WebGLUniformLocation | null = null;
  private uCentersLoc: WebGLUniformLocation | null = null;
  private uDisplacementsLoc: WebGLUniformLocation | null = null;
  private uRadiiLoc: WebGLUniformLocation | null = null;
  private uActiveCountLoc: WebGLUniformLocation | null = null;
  private uTextureLoc: WebGLUniformLocation | null = null;

  // 物理状態
  private springPoints: SpringPoint[] = [];
  private activeDragPoint: SpringPoint | null = null;
  private dragStartUV: { u: number; v: number } | null = null;
  private autoWobbleTime = 0;
  private isLoaded = false;
  private currentImage: HTMLImageElement = new Image();
  private currentSampleIndex = 0;
  private isRunning = false;

  private canvasW = 500;
  private canvasH = 500;

  constructor(container: HTMLElement, uiState: UiState, callbacks: CanvasInteractionCallbacks = {}) {
    this.container = container;
    this.uiState = uiState;
    this.callbacks = callbacks;

    const texts = t().canvas;

    // DOM構築
    this.container.innerHTML = `
      <div class="interaction-hint">
        <span>👆</span> <span id="canvasHintText">${texts.hint}</span>
      </div>

      <div class="canvas-wrapper" id="canvasWrapper">
        <canvas id="glCanvas"></canvas>
        <div class="touch-ring" id="touchRing"></div>
        <div class="wobble-guide-container" id="wobbleGuideContainer">
          <div class="wobble-range-circle" id="wobbleRangeCircle"></div>
          <div class="wobble-pin" id="wobblePin" title="${texts.pinTitle}">
            <div class="wobble-pin-pulse"></div>
            <div class="wobble-pin-icon">📍</div>
          </div>
        </div>
        <div class="watermark-overlay" id="watermarkOverlay">
          ${texts.watermark}
        </div>
      </div>

      <div class="sample-bar" id="sampleBar">
        <span class="sample-label" id="canvasSampleLabel">${texts.sampleLabel}</span>
      </div>

      <!-- 画面下部 フッター（法的表記＆お問い合わせ & ガイド閲覧） -->
      <div class="canvas-legal-footer" id="canvasLegalFooter">
        <a href="#siteGuideSection" id="linkCanvasGuide" style="font-weight:600; color:var(--color-primary);">📖 使い方・FAQ</a>
        <span style="opacity: 0.5;">•</span>
        <a href="/privacy.html" id="linkCanvasPrivacy">${t().legal.footerPrivacy}</a>
        <span style="opacity: 0.5;">•</span>
        <a href="/terms.html" id="linkCanvasTerms">${t().legal.footerTerms}</a>
        <span style="opacity: 0.5;">•</span>
        <a href="https://x.com/KILROYSANWHERE" target="_blank" rel="noopener noreferrer">𝕏 @KILROYSANWHERE</a>
      </div>

      <div class="drop-overlay" id="dropOverlay">
        <div style="font-size: 3rem;">📥</div>
        <div class="drop-text" id="canvasDropText">${texts.dropOverlay}</div>
      </div>
    `;

    this.canvasWrapperEl = this.container.querySelector('#canvasWrapper') as HTMLElement;
    this.canvas = this.container.querySelector('#glCanvas') as HTMLCanvasElement;
    this.touchRingEl = this.container.querySelector('#touchRing') as HTMLElement;
    this.wobbleGuideContainerEl = this.container.querySelector('#wobbleGuideContainer') as HTMLElement;
    this.wobbleRangeCircleEl = this.container.querySelector('#wobbleRangeCircle') as HTMLElement;
    this.wobblePinEl = this.container.querySelector('#wobblePin') as HTMLElement;
    this.watermarkEl = this.container.querySelector('#watermarkOverlay') as HTMLElement;
    this.dropOverlayEl = this.container.querySelector('#dropOverlay') as HTMLElement;
    this.sampleBarEl = this.container.querySelector('#sampleBar') as HTMLElement;
    this.hintTextEl = this.container.querySelector('#canvasHintText') as HTMLElement;
    this.sampleLabelEl = this.container.querySelector('#canvasSampleLabel') as HTMLElement;
    this.dropTextEl = this.container.querySelector('#canvasDropText') as HTMLElement;

    this.initSampleBar();
    this.initWebGL();
    this.bindEvents();

    subscribeLocaleChange(() => {
      this.updateTexts();
    });

    this.uiState.subscribe((state) => {
      this.updateTexts();
      this.watermarkEl.classList.toggle('hidden', !this.uiState.isWatermarkForced());
      this.touchRingEl.style.display = state.touchRingVisible ? '' : 'none';

      const isWobbleActive = state.autoWobble.enabled && this.uiState.isAutoWobbleAllowed();
      const showGuide = state.autoWobble.showGuide && state.activeTab === 'wobble';

      // ユーザー要望:「自動揺れ中にピンが表示されないようにしたい」
      // 普段の自動揺れ中はピンおよび範囲サークルを非表示にし、ガイド表示ONまたはピッキング時のみ表示
      const showPin = isWobbleActive && (showGuide || this.isPickingWobblePos);
      const showCircle = isWobbleActive && (showGuide || this.isTemporarilyShowingRange);

      this.wobblePinEl.classList.toggle('active', showPin);
      this.wobbleRangeCircleEl.classList.toggle('active', showCircle);

      if (isWobbleActive) {
        this.updateWobbleGuidePosition(
          state.autoWobble.centerX ?? 0.5,
          state.autoWobble.centerY ?? 0.55,
          state.autoWobble.radiusPercent ?? 0.18
        );
      }

      if (!state.autoWobble.enabled) {
        this.springPoints = this.springPoints.filter((p) => !p.isAutoWobble);
      }
    });

    this.loadSample(0);
    this.startRenderLoop();
  }

  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  setPickingWobblePos(isPicking: boolean): void {
    this.isPickingWobblePos = isPicking;
    this.canvasWrapperEl.classList.toggle('picking-mode', isPicking);

    const state = this.uiState.getSnapshot();
    const isWobbleActive = state.autoWobble.enabled && this.uiState.isAutoWobbleAllowed();
    const showGuide = state.autoWobble.showGuide && state.activeTab === 'wobble';
    this.wobblePinEl.classList.toggle('active', isWobbleActive && (showGuide || isPicking));
  }

  showTemporaryRangeGuide(): void {
    this.isTemporarilyShowingRange = true;
    const state = this.uiState.getSnapshot();
    const isWobbleActive = state.autoWobble.enabled && this.uiState.isAutoWobbleAllowed();
    if (isWobbleActive) {
      this.wobbleRangeCircleEl.classList.add('active');
      this.updateWobbleGuidePosition(
        state.autoWobble.centerX ?? 0.5,
        state.autoWobble.centerY ?? 0.55,
        state.autoWobble.radiusPercent ?? 0.18
      );
    }
    if (this.tempRangeTimer) window.clearTimeout(this.tempRangeTimer);
    this.tempRangeTimer = window.setTimeout(() => {
      this.isTemporarilyShowingRange = false;
      const curState = this.uiState.getSnapshot();
      const stillShowCircle = curState.autoWobble.enabled &&
        this.uiState.isAutoWobbleAllowed() &&
        curState.autoWobble.showGuide &&
        curState.activeTab === 'wobble';
      this.wobbleRangeCircleEl.classList.toggle('active', stillShowCircle);
    }, 1200);
  }

  private updateWobbleGuidePosition(u: number, v: number, radiusPercent: number): void {
    const w = this.canvas.clientWidth || this.canvasW;
    const h = this.canvas.clientHeight || this.canvasH;
    const px = u * w;
    const py = v * h;

    this.wobblePinEl.style.left = `${px}px`;
    this.wobblePinEl.style.top = `${py}px`;

    // シェーダーの物理影響範囲（ピクセル単位: 半径 r * h）
    const rPx = radiusPercent * h;
    const diameter = Math.round(rPx * 2);
    this.wobbleRangeCircleEl.style.left = `${px}px`;
    this.wobbleRangeCircleEl.style.top = `${py}px`;
    this.wobbleRangeCircleEl.style.width = `${diameter}px`;
    this.wobbleRangeCircleEl.style.height = `${diameter}px`;
  }

  // --- WebGL初期化 ---
  private initWebGL(): void {
    const gl = this.canvas.getContext('webgl', { alpha: false, antialias: true }) ||
      (this.canvas.getContext('experimental-webgl') as WebGLRenderingContext | null);

    if (!gl) {
      console.error('[PurinPurin UI] WebGL is not supported on this device.');
      return;
    }
    this.gl = gl;

    const vsSource = `
      attribute vec2 aPosition;
      varying vec2 vUv;
      void main() {
        vUv = (aPosition + 1.0) * 0.5;
        vUv.y = 1.0 - vUv.y;
        gl_Position = vec4(aPosition, 0.0, 1.0);
      }
    `;

    const fsSource = `
      precision highp float;
      varying vec2 vUv;
      uniform sampler2D uTexture;
      uniform vec2 uResolution;
      
      const int MAX_POINTS = 8;
      uniform vec2 uCenters[MAX_POINTS];
      uniform vec2 uDisplacements[MAX_POINTS];
      uniform float uRadii[MAX_POINTS];
      uniform int uActiveCount;

      void main() {
        vec2 aspect = vec2(uResolution.x / uResolution.y, 1.0);
        vec2 totalDisp = vec2(0.0);

        for (int i = 0; i < MAX_POINTS; i++) {
          if (i >= uActiveCount) break;
          vec2 diff = (vUv - uCenters[i]) * aspect;
          float dist = length(diff);
          float r = uRadii[i];

          if (dist < r) {
            float normDist = dist / r;
            float w = (1.0 - normDist * normDist);
            w = w * w;

            vec2 disp = uDisplacements[i];
            float dispLen = length(disp);
            float maxDisp = r * 0.42;
            if (dispLen > maxDisp && dispLen > 0.0001) {
              disp = disp * (maxDisp / dispLen);
            }

            totalDisp += disp * w;
          }
        }

        vec2 sampleUv = clamp(vUv - totalDisp, 0.0005, 0.9995);
        gl_FragColor = texture2D(uTexture, sampleUv);
      }
    `;

    const createShader = (type: number, source: string): WebGLShader | null => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error(gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vertexShader = createShader(gl.VERTEX_SHADER, vsSource);
    const fragmentShader = createShader(gl.FRAGMENT_SHADER, fsSource);
    if (!vertexShader || !fragmentShader) return;

    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(program));
      return;
    }
    this.program = program;
    gl.useProgram(program);

    // フルスクリーンQuadバッファ
    const quadVertices = new Float32Array([
      -1, -1,
       1, -1,
      -1,  1,
      -1,  1,
       1, -1,
       1,  1
    ]);
    const quadBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, quadVertices, gl.STATIC_DRAW);

    const aPosition = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(aPosition);
    gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 0, 0);

    // Uniformロケーション
    this.uTextureLoc = gl.getUniformLocation(program, 'uTexture');
    this.uResolutionLoc = gl.getUniformLocation(program, 'uResolution');
    this.uCentersLoc = gl.getUniformLocation(program, 'uCenters');
    this.uDisplacementsLoc = gl.getUniformLocation(program, 'uDisplacements');
    this.uRadiiLoc = gl.getUniformLocation(program, 'uRadii');
    this.uActiveCountLoc = gl.getUniformLocation(program, 'uActiveCount');

    // テクスチャ
    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  }

  private updateTexts(): void {
    const texts = t().canvas;
    const textsLegal = t().legal;
    if (this.hintTextEl) this.hintTextEl.textContent = texts.hint;
    if (this.sampleLabelEl) this.sampleLabelEl.textContent = texts.sampleLabel;
    if (this.dropTextEl) this.dropTextEl.textContent = texts.dropOverlay;
    if (this.watermarkEl) this.watermarkEl.textContent = texts.watermark;
    if (this.wobblePinEl) this.wobblePinEl.title = texts.pinTitle;
    const privLink = this.container.querySelector('#linkCanvasPrivacy');
    if (privLink) privLink.textContent = textsLegal.footerPrivacy;
    const termsLink = this.container.querySelector('#linkCanvasTerms');
    if (termsLink) termsLink.textContent = textsLegal.footerTerms;
    const guideLink = this.container.querySelector('#linkCanvasGuide');
    if (guideLink) guideLink.textContent = texts.guideLink || '📖 使い方・FAQ';
    const sampleNames = [texts.sampleGirl || '女の子', texts.samplePudding, texts.samplePeach, texts.sampleNikukyu || '肉球'];
    this.sampleBarEl?.querySelectorAll('.sample-thumb').forEach((el, idx) => {
      if (sampleNames[idx]) (el as HTMLElement).title = sampleNames[idx];
    });
  }

  private initSampleBar(): void {
    SAMPLE_IMAGES.forEach((sample, idx) => {
      const thumb = document.createElement('div');
      thumb.className = `sample-thumb ${idx === 0 ? 'active' : ''}`;
      thumb.title = sample.name;
      thumb.innerHTML = sample.icon;
      thumb.addEventListener('click', () => {
        this.loadSample(idx);
      });
      this.sampleBarEl.appendChild(thumb);
    });
  }

  // --- 画像ロード ---
  loadSample(index: number): void {
    if (index < 0 || index >= SAMPLE_IMAGES.length) return;
    this.currentSampleIndex = index;
    const sample = SAMPLE_IMAGES[index];

    this.sampleBarEl.querySelectorAll('.sample-thumb').forEach((el, idx) => {
      el.classList.toggle('active', idx === index);
    });

    this.loadImageFromSrc(sample.src);
    this.callbacks.onSampleChanged?.(index);
  }

  loadImageFromFile(file: File): void {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        this.sampleBarEl.querySelectorAll('.sample-thumb').forEach((el) => el.classList.remove('active'));
        this.loadImageFromSrc(e.target.result as string);
        this.callbacks.onFileLoaded?.(file);
      }
    };
    reader.readAsDataURL(file);
  }

  loadImageFromUrl(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoaded = false;
      this.springPoints = [];
      this.activeDragPoint = null;

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        this.sampleBarEl.querySelectorAll('.sample-thumb').forEach((el) => el.classList.remove('active'));
        this.currentImage = img;
        this.setupCanvasSize();
        this.updateTexture(img);
        this.isLoaded = true;
        resolve();
      };
      img.onerror = () => {
        reject(new Error(t().urlModal.errFailed || '画像の読み込みに失敗しました。外部サイトのアクセス制限(CORS)により保護されている可能性があります。画像を右クリックして「画像をコピー」し、画面上で Ctrl+V (ペースト) すると直接読み込めます！'));
      };
      img.src = url;
    });
  }

  private loadImageFromSrc(src: string): void {
    this.isLoaded = false;
    this.springPoints = [];
    this.activeDragPoint = null;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      this.currentImage = img;
      this.setupCanvasSize();
      this.updateTexture(img);
      this.isLoaded = true;
    };
    img.src = src;
  }

  private updateTexture(image: HTMLImageElement): void {
    if (!this.gl || !this.texture) return;
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture);
    this.gl.pixelStorei(this.gl.UNPACK_FLIP_Y_WEBGL, false);
    this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA, this.gl.UNSIGNED_BYTE, image);
  }

  private setupCanvasSize(): void {
    const containerW = this.container.clientWidth || window.innerWidth;
    const containerH = this.container.clientHeight || (window.innerHeight - 80);

    const maxW = Math.min(containerW * 0.92, 540);
    const maxH = Math.min(containerH * 0.88, 540);

    const w = this.currentImage.width || 500;
    const h = this.currentImage.height || 500;

    const scale = Math.min(maxW / w, maxH / h, 1);
    this.canvasW = Math.round(w * scale);
    this.canvasH = Math.round(h * scale);

    this.canvas.width = this.canvasW;
    this.canvas.height = this.canvasH;
    this.canvas.style.width = this.canvasW + 'px';
    this.canvas.style.height = this.canvasH + 'px';

    if (this.gl) {
      this.gl.viewport(0, 0, this.canvasW, this.canvasH);
    }
  }

  // --- イベントバインド ---
  private bindEvents(): void {
    window.addEventListener('resize', () => {
      if (this.isLoaded) {
        this.setupCanvasSize();
      }
    });

    const getUV = (e: MouseEvent | TouchEvent) => {
      const rect = this.canvas.getBoundingClientRect();
      const clientX = 'touches' in e && e.touches.length > 0 ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e && e.touches.length > 0 ? e.touches[0].clientY : (e as MouseEvent).clientY;
      const x = (clientX - rect.left) / rect.width;
      const y = (clientY - rect.top) / rect.height;
      return {
        u: Math.max(0, Math.min(1, x)),
        v: Math.max(0, Math.min(1, y)),
        rawX: clientX - rect.left,
        rawY: clientY - rect.top,
      };
    };

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (e.cancelable && 'touches' in e) e.preventDefault();
      const pos = getUV(e);
      if (pos.u <= 0 || pos.u >= 1 || pos.v <= 0 || pos.v >= 1) return;

      if (this.isPickingWobblePos) {
        this.setPickingWobblePos(false);
        this.uiState.setAutoWobbleCenter(pos.u, pos.v);
        this.callbacks.onWobblePosPicked?.(pos.u, pos.v);
        return;
      }

      this.dragStartUV = { u: pos.u, v: pos.v };

      if (this.springPoints.length >= 8) {
        this.springPoints.shift();
      }

      const state = this.uiState.getSnapshot();
      this.activeDragPoint = {
        cx: pos.u,
        cy: pos.v,
        dx: 0,
        dy: 0,
        vx: 0,
        vy: 0.008,
        radius: state.physics.radiusPercent,
        isDragging: true,
      };
      this.springPoints.push(this.activeDragPoint);
 
      if (state.touchRingVisible) {
        this.touchRingEl.style.left = pos.rawX + 'px';
        this.touchRingEl.style.top = pos.rawY + 'px';
        this.touchRingEl.classList.add('active');
        this.createRipple(pos.rawX, pos.rawY);
      }

      this.callbacks.onPhysicsImpulse?.('poke');
    };

    const onPointerMove = (e: MouseEvent | TouchEvent) => {
      if (!this.activeDragPoint || !this.dragStartUV) return;
      if (e.cancelable && 'touches' in e) e.preventDefault();
      const pos = getUV(e);

      const rawDeltaU = pos.u - this.dragStartUV.u;
      const rawDeltaV = pos.v - this.dragStartUV.v;

      const rawDist = Math.hypot(rawDeltaU, rawDeltaV);
      const maxDisp = this.activeDragPoint.radius * 0.38;

      let clampedDx = 0;
      let clampedDy = 0;
      if (rawDist > 0.00001) {
        const satDist = maxDisp * Math.tanh(rawDist / maxDisp);
        clampedDx = (rawDeltaU / rawDist) * satDist;
        clampedDy = (rawDeltaV / rawDist) * satDist;
      }

      const prevDx = this.activeDragPoint.dx;
      const prevDy = this.activeDragPoint.dy;

      this.activeDragPoint.dx = clampedDx;
      this.activeDragPoint.dy = clampedDy;

      this.activeDragPoint.vx = (this.activeDragPoint.dx - prevDx) * 0.6;
      this.activeDragPoint.vy = (this.activeDragPoint.dy - prevDy) * 0.6;

      if (this.uiState.getSnapshot().touchRingVisible) {
        this.touchRingEl.style.left = pos.rawX + 'px';
        this.touchRingEl.style.top = pos.rawY + 'px';
      }
    };

    const onPointerUp = () => {
      if (this.activeDragPoint) {
        this.activeDragPoint.isDragging = false;
        this.activeDragPoint = null;
        this.callbacks.onPhysicsImpulse?.('bounce');
      }
      this.dragStartUV = null;
      this.touchRingEl.classList.remove('active');
    };

    this.canvas.addEventListener('mousedown', onPointerDown);
    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('mouseup', onPointerUp);

    this.canvas.addEventListener('touchstart', onPointerDown, { passive: false });
    window.addEventListener('touchmove', onPointerMove, { passive: false });
    window.addEventListener('touchend', onPointerUp);
    window.addEventListener('touchcancel', onPointerUp);

    // 📍 揺らす位置ピンのドラッグ操作
    let isDraggingPin = false;

    const onPinPointerDown = (e: MouseEvent | TouchEvent) => {
      e.stopPropagation();
      if (e.cancelable) e.preventDefault();
      isDraggingPin = true;
    };

    const onPinPointerMove = (e: MouseEvent | TouchEvent) => {
      if (!isDraggingPin) return;
      if (e.cancelable && 'touches' in e) e.preventDefault();
      const rect = this.canvas.getBoundingClientRect();
      const clientX = 'touches' in e && e.touches.length > 0 ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e && e.touches.length > 0 ? e.touches[0].clientY : (e as MouseEvent).clientY;
      const rawX = clientX - rect.left;
      const rawY = clientY - rect.top;
      const u = Math.max(0.05, Math.min(0.95, rawX / rect.width));
      const v = Math.max(0.05, Math.min(0.95, rawY / rect.height));
      this.uiState.setAutoWobbleCenter(u, v);
    };

    const onPinPointerUp = () => {
      isDraggingPin = false;
    };

    this.wobblePinEl.addEventListener('mousedown', onPinPointerDown);
    window.addEventListener('mousemove', onPinPointerMove);
    window.addEventListener('mouseup', onPinPointerUp);

    this.wobblePinEl.addEventListener('touchstart', onPinPointerDown, { passive: false });
    window.addEventListener('touchmove', onPinPointerMove, { passive: false });
    window.addEventListener('touchend', onPinPointerUp);
    window.addEventListener('touchcancel', onPinPointerUp);

    // ドラッグ＆ドロップファイル受け入れ
    window.addEventListener('dragover', (e) => {
      e.preventDefault();
      this.dropOverlayEl.classList.add('active');
    });
    window.addEventListener('dragleave', (e) => {
      if (e.relatedTarget === null) {
        this.dropOverlayEl.classList.remove('active');
      }
    });
    window.addEventListener('drop', (e) => {
      e.preventDefault();
      this.dropOverlayEl.classList.remove('active');
      if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
        this.loadImageFromFile(e.dataTransfer.files[0]);
      } else if (e.dataTransfer) {
        const text = e.dataTransfer.getData('text/uri-list') || e.dataTransfer.getData('text/plain');
        if (text && (text.startsWith('http://') || text.startsWith('https://') || text.startsWith('data:image/'))) {
          const url = text.trim();
          if (!this.uiState.isUrlImportAllowed()) {
            this.callbacks.onUpgradeRequested?.(t().upgrade.perkUrl);
            return;
          }
          this.loadImageFromUrl(url).catch((err) => {
            alert(err.message);
          });
        }
      }
    });

    // クリップボードからの画像貼り付け (Ctrl + V / Command + V)
    window.addEventListener('paste', (e: ClipboardEvent) => {
      // テキスト入力欄（モーダルのURL入力やプロンプト等）にフォーカスがある場合は通常のテキスト貼り付けを優先
      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || (activeEl as HTMLElement).isContentEditable)) {
        return;
      }

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            this.loadImageFromFile(file);
            console.log('[CanvasInteraction] Loaded image from clipboard paste (Ctrl+V):', file.name, file.type, file.size);
            break;
          }
        }
      }
    });
  }

  // --- 波紋エフェクト ---
  private createRipple(x: number, y: number): void {
    const ripple = document.createElement('div');
    ripple.className = 'impact-ripple';
    ripple.style.left = x + 'px';
    ripple.style.top = y + 'px';
    ripple.style.width = '60px';
    ripple.style.height = '60px';
    this.canvasWrapperEl.appendChild(ripple);
    setTimeout(() => ripple.remove(), 500);
  }

  // --- ポンと揺らす (外部トリガー可能) ---
  triggerPoke(): void {
    if (this.springPoints.length >= 8) this.springPoints.shift();
    const state = this.uiState.getSnapshot();
    const p: SpringPoint = {
      cx: 0.5,
      cy: 0.55,
      dx: 0,
      dy: 0.02,
      vx: 0,
      vy: 0.03,
      radius: state.physics.radiusPercent * 1.2,
      isDragging: false,
    };
    this.springPoints.push(p);
    this.callbacks.onPhysicsImpulse?.('poke');
  }

  // --- 物理更新ループ ---
  private updatePhysics(): void {
    const state = this.uiState.getSnapshot();
    const k = state.physics.stiffness;
    const d = state.physics.damping;

    // 自動揺れ (US-10)
    if (state.autoWobble.enabled && this.uiState.isAutoWobbleAllowed()) {
      this.autoWobbleTime += 0.016 * state.autoWobble.speed;
      let wobblePoint = this.springPoints.find((p) => p.isAutoWobble);
      const targetCx = state.autoWobble.centerX ?? 0.5;
      const targetCy = state.autoWobble.centerY ?? 0.55;

      const targetRadius = state.autoWobble.radiusPercent ?? 0.18;

      if (!wobblePoint) {
        if (this.springPoints.length >= 8) this.springPoints.shift();
        wobblePoint = {
          cx: targetCx,
          cy: targetCy,
          dx: 0,
          dy: 0,
          vx: 0,
          vy: 0,
          radius: targetRadius,
          isDragging: false,
          isAutoWobble: true,
        };
        this.springPoints.push(wobblePoint);
      } else {
        wobblePoint.cx = targetCx;
        wobblePoint.cy = targetCy;
        wobblePoint.radius = targetRadius;
      }

      if (state.autoWobble.mode === 'breath') {
        const wave = Math.sin(this.autoWobbleTime * Math.PI * 2 * 0.4) * state.autoWobble.intensity * 0.003;
        wobblePoint.vy += wave;
      } else if (state.autoWobble.mode === 'heartbeat') {
        const phase = (this.autoWobbleTime % 1.2) / 1.2;
        if (phase < 0.15) {
          wobblePoint.vy += Math.sin((phase / 0.15) * Math.PI) * state.autoWobble.intensity * 0.008;
        }
      } else if (state.autoWobble.mode === 'tremble') {
        wobblePoint.vx += (Math.random() - 0.5) * state.autoWobble.intensity * 0.004;
        wobblePoint.vy += (Math.random() - 0.5) * state.autoWobble.intensity * 0.004;
      }
    }

    for (let i = this.springPoints.length - 1; i >= 0; i--) {
      const p = this.springPoints[i];
      if (!p.isDragging) {
        const fx = -k * p.dx;
        const fy = -k * p.dy;

        p.vx = (p.vx + fx) * d;
        p.vy = (p.vy + fy) * d;

        p.dx += p.vx;
        p.dy += p.vy;

        // 物理振動中の変位安全ガード
        const maxDisp = p.radius * 0.40;
        const currentDist = Math.hypot(p.dx, p.dy);
        if (currentDist > maxDisp) {
          p.dx = (p.dx / currentDist) * maxDisp;
          p.dy = (p.dy / currentDist) * maxDisp;
        }

        // 揺れの自動収束消去
        if (!p.isAutoWobble &&
            Math.abs(p.dx) < 0.0001 && Math.abs(p.dy) < 0.0001 &&
            Math.abs(p.vx) < 0.0001 && Math.abs(p.vy) < 0.0001) {
          this.springPoints.splice(i, 1);
        }
      }
    }
  }

  // --- 描画ループ ---
  private startRenderLoop(): void {
    this.isRunning = true;
    const render = () => {
      if (!this.isRunning) return;
      this.updatePhysics();

      if (this.isLoaded && this.gl && this.program && this.texture) {
        this.gl.viewport(0, 0, this.canvasW, this.canvasH);
        this.gl.clearColor(1, 1, 1, 1);
        this.gl.clear(this.gl.COLOR_BUFFER_BIT);

        this.gl.useProgram(this.program);
        this.gl.uniform2f(this.uResolutionLoc, this.canvasW, this.canvasH);

        const count = Math.min(this.springPoints.length, 8);
        this.gl.uniform1i(this.uActiveCountLoc, count);

        if (count > 0) {
          const centers: number[] = [];
          const disps: number[] = [];
          const radii: number[] = [];

          for (let i = 0; i < count; i++) {
            const p = this.springPoints[i];
            centers.push(p.cx, p.cy);
            disps.push(p.dx, p.dy);
            radii.push(p.radius);
          }

          this.gl.uniform2fv(this.uCentersLoc, new Float32Array(centers));
          this.gl.uniform2fv(this.uDisplacementsLoc, new Float32Array(disps));
          this.gl.uniform1fv(this.uRadiiLoc, new Float32Array(radii));
        }

        this.gl.activeTexture(this.gl.TEXTURE0);
        this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture);
        this.gl.uniform1i(this.uTextureLoc, 0);

        this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);
      }

      requestAnimationFrame(render);
    };

    requestAnimationFrame(render);
  }

  destroy(): void {
    this.isRunning = false;
  }
}
